import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import website from '@/const/website'

const TAGS_STORAGE_KEY = `${website.key}_tags`

export interface TagItem {
  path: string
  name: string
  title: string
  query?: Record<string, string>
}

/** 判断路径是否在允许标签列表中（不添加标签的页面） */
function isAllowTag(path: string): boolean {
  return !website.allowTagList.some((item) => {
    if (item.endsWith('/*')) {
      return path.startsWith(item.slice(0, -1))
    }
    return path === item
  })
}

export const useTagsStore = defineStore('tags', () => {
  const route = useRoute()
  const router = useRouter()

  /** 标签列表 */
  const tags = ref<TagItem[]>([])

  /** 从 localStorage 恢复标签 */
  function restoreTags() {
    try {
      const stored = localStorage.getItem(TAGS_STORAGE_KEY)
      if (stored) {
        tags.value = JSON.parse(stored)
      }
    } catch {
      tags.value = []
    }
  }

  /** 监听变化自动持久化 */
  watch(
    () => tags.value,
    (val) => {
      localStorage.setItem(TAGS_STORAGE_KEY, JSON.stringify(val))
    },
    { deep: true }
  )

  /** 添加标签（路由切换时调用） */
  function addTag() {
    const { path, name, query, meta } = route
    // 排除：无名称、allowTagList 白名单页面、hidden 路由（新增/编辑/详情等子页面）
    if (!name || !isAllowTag(path) || meta?.hidden) return

    const title = (meta?.title as string) || (name as string)
    const exists = tags.value.some((t) => t.path === path && t.name === String(name))

    if (!exists) {
      tags.value.push({
        path,
        name: String(name),
        title,
        query: query as Record<string, string>,
      })
    }
  }

  /** 关闭指定标签 */
  function closeTag(tag: TagItem) {
    const index = tags.value.findIndex((t) => t.path === tag.path && t.name === tag.name)
    if (index === -1) return

    tags.value.splice(index, 1)

    // 如果关闭的是当前页面，跳转到最后一个标签或首页
    if (route.path === tag.path && route.name === tag.name) {
      const last = tags.value[tags.value.length - 1]
      if (last) {
        router.push({ path: last.path, query: last.query })
      } else {
        router.push(website.firstPage)
      }
    }
  }

  /** 关闭其他标签（保留当前和首页） */
  function closeOther() {
    const currentPath = route.path
    const firstName = website.firstPage.name as string
    // 保留首页 + 当前页
    tags.value = tags.value.filter(
      (t) =>
        t.name === firstName ||
        (t.path === currentPath && t.name === String(route.name))
    )
  }

  /** 关闭所有标签，跳转首页 */
  function closeAll() {
    tags.value = []
    router.push(website.firstPage)
  }

  /** 重置标签（登录时调用） */
  function resetTags() {
    tags.value = []
    localStorage.removeItem(TAGS_STORAGE_KEY)
  }

  return {
    tags,
    restoreTags,
    addTag,
    closeTag,
    closeOther,
    closeAll,
    resetTags,
  }
})
